import { useSelector } from 'react-redux';
import React, { Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import Cookies from 'js-cookie';

import { refreshToken } from '@/utils/auth';

import { RootState, useAppDispatch } from '@/store/store';
import { setAuth, setInitialized } from '@/store/slices/AuthSlice/AuthSlice';

import Login from '../Auth/Login/Login';
import Loading from '../Loading/Loading';
import ProtectedRoute from '../ProtectedRoute/ProtectedRoute';
import PaymentResult from '../Pay/PaymentResult/PaymentResult';

const Main = React.lazy(() => import('@components/Main/Main'));
const PayMethod = React.lazy(() => import('../Pay/PayMethod/PayMethod'));
const TooltipModal = React.lazy(() => import('@components/TooltipModal/TooltipModal'));
const RecoverPage = React.lazy(() => import('@components/Auth/RecoverPage/RecoverPage'));
const AdminMain = React.lazy(() => import('@components/AdminSection/AdminMain/AdminMain'));
const ChangePassword = React.lazy(() => import('@components/Auth/ChangePassword/ChangePassword'));
const AddItem = React.lazy(() => import('@components/AdminSection/AdminContent/components/AddItem'));
const ContetnItem = React.lazy(() => import('@components/AdminSection/AdminContent/components/ContetnItem'));

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
	const role = Cookies.get('user_role');
	if (role !== 'admin') return <Navigate to='/service' replace />;
	return <>{children}</>;
};

function App() {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();

	const { isAuth, role, initialized } = useSelector((state: RootState) => state.auth);

	useEffect(() => {
		const sessionId = Cookies.get('session_id');
		const role = Cookies.get('user_role') as 'admin' | 'user' | null;

		if (sessionId && role) {
			dispatch(setAuth({ role }));
		}
		dispatch(setInitialized());
	}, [dispatch]);

	useEffect(() => {
		if (location.pathname === '/service/pay/pmethod' && !location.state) {
			navigate('/service/pay', { replace: true });
		}
	}, [location, navigate]);

	useEffect(() => {
		function getExpireTimestamp(dt: string) {
			const legacyDate = dt.match(/^(\d{2}):(\d{2}):(\d{2}) (\d{2})\.(\d{2})\.(\d{4})$/);
			if (!legacyDate) return Date.parse(dt);

			const [, hours, minutes, seconds, day, month, year] = legacyDate.map(Number);
			return new Date(year, month - 1, day, hours, minutes, seconds).getTime();
		}

		const interval = setInterval(async () => {
			const expireDt = Cookies.get('expire_dt');

			if (!expireDt) return;

			const expireTime = getExpireTimestamp(expireDt);
			if (!Number.isFinite(expireTime)) return;

			const now = Date.now();

			if (expireTime - now < 60_000) {
				await refreshToken();
			}
		}, 30_000);

		return () => clearInterval(interval);
	}, []);

	if (!initialized) return <Loading />;

	return (
		<Suspense fallback={<Loading />}>
			<Routes>
				<Route path='/authenticate' element={<Login key={location.pathname} />} />
				<Route
					path='/service/*'
					element={
						<ProtectedRoute>
							<Main />
						</ProtectedRoute>
					}
				/>
				<Route path='/service/pay/pmethod' element={<PayMethod />} />
				<Route
					path='/admin/*'
					element={
						<AdminRoute>
							<AdminMain />
						</AdminRoute>
					}
				/>
				<Route
					path='/admin/faq/:id'
					element={
						<AdminRoute>
							<ContetnItem mode='faq' />
						</AdminRoute>
					}
				/>
				<Route
					path='/admin/news/:id'
					element={
						<AdminRoute>
							<ContetnItem mode='new' />
						</AdminRoute>
					}
				/>
				<Route
					path='/admin/faq/new'
					element={
						<AdminRoute>
							<AddItem />
						</AdminRoute>
					}
				/>
				<Route
					path='/admin/news/new'
					element={
						<AdminRoute>
							<AddItem />
						</AdminRoute>
					}
				/>

				<Route path='/password/get-token' element={<RecoverPage />} />
				<Route path='/password/new-password' element={<ChangePassword />} />
				<Route path='/pay/res' element={<PaymentResult />} />
				<Route
					path='/'
					element={
						isAuth ? (
							role === 'admin' ? (
								<Navigate to='/admin' replace />
							) : (
								<Navigate to='/service' replace />
							)
						) : (
							<Navigate to='/authenticate' replace />
						)
					}
				/>
				<Route
					path='*'
					element={
						isAuth ? (
							role === 'admin' ? (
								<Navigate to='/admin' replace />
							) : (
								<Navigate to='/service' replace />
							)
						) : (
							<Navigate to='/authenticate' replace />
						)
					}
				/>
			</Routes>
			<TooltipModal />
		</Suspense>
	);
}

export default App;
